import { library } from '@fortawesome/fontawesome-svg-core';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { useTranslator } from '@Localization';
import { IconButton } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { S_PaginationProps } from '../../../../design/pagination/S_Pagination';
import { TrashIcon } from '../../../../design/static/icons/TrashIcon';
import { GTable, IGColumnProps } from '../../../components/table/GTable';
import { enumDirectionToStringOrder, stringOrderToEnumDirection } from '../../../components/table/TableHelpers';
import { EnableFor } from '../../../containers/EnableForRole';
import { sortBy } from '../../../helpers/collections.helper';
import { toLocaleDateString, toLocaleDateTimeString } from '../../../helpers/date.helpers';
import { SortingDirection } from '../../../models/sortingDirection.model';
import { IUserDetails } from '../projectDetails.models';
import { useProjectDetailsPermissions } from '../projectDetails.permissions';

library.add(faEdit);

interface IUsersListProps {
  users: IUserDetails[];
  orderBy: string;
  orderDirection: SortingDirection | undefined;
  isLoading: boolean;
  onRemoveUser(userId: string): void;
  onSortChange(orderBy: string, orderDirection: SortingDirection | undefined): void;
  pagination: S_PaginationProps;
}

export const UsersList = observer((props: IUsersListProps): ReactElement => {
  const { t } = useTranslator();
  const projectDetailsPermissions = useProjectDetailsPermissions();
  const canRemoveUserFromProject = projectDetailsPermissions.canManageUsers();

  const joinDateRenderer = (data: IUserDetails): ReactElement => <div>{data.joinDate ? toLocaleDateString(data.joinDate) : ''}</div>;

  const lastLoginDateRenderer = (data: IUserDetails): ReactElement => <div>{data.lastLoginDate ? toLocaleDateTimeString(data.lastLoginDate) : ''}</div>;

  const actionsRenderer = (data: IUserDetails): ReactElement => (
    <>
      <EnableFor hasPermission={canRemoveUserFromProject}>
        <IconButton
          size='sm'
          onClick={(): void => {
            props.onRemoveUser(data.id);
          }}
          title={t('remove_user')}
        >
          <TrashIcon />
        </IconButton>
      </EnableFor>
    </>
  );

  const handleSortChange = (orderBy: string, orderDirection: string): void => {
    props.onSortChange(orderBy, stringOrderToEnumDirection(orderDirection));
  };

  const gColumns: IGColumnProps<IUserDetails>[] = [
    {
      sortBy: 'email',
      headerName: t('email_address'),
      renderer: (x) => x.email,
    },
    {
      sortBy: 'role',
      headerName: t('role'),
      renderer: (x) => x.role,
    },
    {
      sortBy: 'totalReviewed',
      headerName: t('total_reviewed'),
      headerTooltip: t('total_reviewed_tooltip'),
      renderer: (x) => x.totalReviewed,
    },
    {
      sortBy: 'totalSubmitted',
      headerName: t('total_submitted'),
      headerTooltip: t('total_submitted_tooltip'),
      renderer: (x) => x.totalSubmitted,
    },
    {
      sortBy: 'joinDate',
      headerName: t('date_joined'),
      renderer: joinDateRenderer,
    },
    {
      sortBy: 'lastLoginDate',
      headerName: t('last_login_date'),
      renderer: lastLoginDateRenderer,
    },
    {
      headerName: t('actions'),
      renderer: actionsRenderer,
      actions: true,
      width: 'min-content',
    },
  ];

  const sorted = sortBy(props.users, props.orderBy, props.orderDirection);

  return (
    <GTable<IUserDetails>
      isLoading={props.isLoading}
      noItemsPlaceholder={t('there_are_no_active_collaborators')}
      columns={gColumns}
      items={sorted}
      onSortChanged={handleSortChange}
      sortingModel={[{ orderBy: props.orderBy, orderType: enumDirectionToStringOrder(props.orderDirection) }]}
      paginationProps={props.pagination}
    />
  );
});
